import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Moon,
  Package,
  Building2,
  Users,
  GraduationCap,
  Hash,
  Sparkles,
  AlertTriangle,
  UserCheck,
  ExternalLink,
  UserRound,
  MapPin,
  Phone,
  RotateCcw,
  Ban,
  type LucideIcon,
} from "lucide-react";
import { StatusBadge } from "@/components/ui/StatusBadge";
import {
  displayCustomerName,
  resolveWindowAndPriority,
  extractJobPreview,
} from "@/lib/format";
import { STATUS_DONE, type Dispatch } from "@/lib/types";
import {
  isPseudoDispatch,
  windowColorClasses,
  detectPseudoKind,
  usePlusAgreements,
  type PseudoKind,
} from "./api";
import { useConflict } from "./conflicts";
import { FromDispatchTicketModal } from "./PendingStrip";
import { useFieldStatusMap, chicagoTodayISO, FieldChip } from "@/features/mobile/field";
import {
  useDispatchActionMap,
  useVoidDispatchAction,
  CancelDispatchModal,
} from "./dispatchActions";
import { cn } from "@/lib/cn";

interface Props {
  dispatch: Dispatch;
  onClick: (d: Dispatch) => void;
  /** compact = ESC-EDB-style full-color block (Seth, 2026-06-09): name · city ·
   * window · priority, no icon chrome — built so the WHOLE roster fits on one
   * screen. Hover card, click and right-click menu all still work. */
  density?: "compact" | "cards";
}

function pseudoIcon(kind: PseudoKind): LucideIcon {
  switch (kind) {
    case "off":
      return Moon;
    case "parts":
      return Package;
    case "office":
      return Building2;
    case "meeting":
      return Users;
    case "training":
      return GraduationCap;
    default:
      return Hash;
  }
}

export function DispatchBlock({ dispatch, onClick, density = "cards" }: Props) {
  const conflict = useConflict(dispatch.dispatch, dispatch.counter);
  const isPseudo = isPseudoDispatch(dispatch);
  const pseudoKind = detectPseudoKind(dispatch);
  // FIELD-M1a: the tech's live state from Pegasus Field (OTW / On site / Wrapped).
  const fieldRow = useFieldStatusMap(chicagoTodayISO()).data?.get(dispatch.dispatch);
  // CANCEL-01: is there a Pegasus cancellation recorded against this dispatch?
  const cancelAction = useDispatchActionMap().data?.get(dispatch.dispatch);
  const voidAction = useVoidDispatchAction();
  const [showCancel, setShowCancel] = useState(false);

  // Rich hover card (Task #80) — replaces the browser `title` tooltip. Shows
  // after a short hover with the FULL notes + address, rendered through a
  // portal so column overflow / the card's hover-transform can't clip it.
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(
    null,
  );
  const hoverTimer = useRef<number | undefined>(undefined);
  const startHover = (e: React.MouseEvent) => {
    const x = e.clientX;
    const y = e.clientY;
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => setHoverPos({ x, y }), 350);
  };
  const endHover = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    setHoverPos(null);
  };

  // Right-click quick actions (the other half of Task #80). Esc or click-away
  // closes; pseudo blocks (Off Today / Parts) keep the browser menu.
  const navigate = useNavigate();
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  // Quick part/return tickets born from this dispatch (TICKET-01b).
  const [ticketKind, setTicketKind] = useState<
    "order_parts" | "return_visit" | null
  >(null);
  useEffect(() => {
    if (!menu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenu(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menu]);
  const PseudoIcon = pseudoKind ? pseudoIcon(pseudoKind) : null;
  const customer = displayCustomerName(
    dispatch.locname,
    dispatch.customer_fullname,
  );
  const { timeWindow, priority } = resolveWindowAndPriority(
    dispatch.window_code,
    dispatch.notes,
  );
  // The "primary" theme color for the left stripe comes from priority if
  // there is one (TECH/LONG/etc), else from the time window, else default.
  const stripeColors = windowColorClasses(priority ?? timeWindow);
  const windowColors = timeWindow ? windowColorClasses(timeWindow) : null;
  const status = dispatch.status ?? "Pending";
  const isDone = STATUS_DONE.has(status);
  // BOARD-HD v2 (Seth 2026-09-01: "Don't need promised time. Hate how the
  // names are cut off.") — the card carries the street address, the estimated
  // length, a gold PLUS chip for plan members, and a two-line job preview.
  // The NAME and ADDRESS wrap instead of truncating — nothing on a card gets
  // cut off; the time window pill already says when.
  const preview = extractJobPreview(dispatch.notes, 120);
  const plusSet = usePlusAgreements().data;
  const isPlus = !isPseudo && !!plusSet?.has(dispatch.custno);
  const est = dispatch.tech_est_hours
    ? `${dispatch.tech_est_hours}h`
    : dispatch.dispatch_est_min
      ? `${dispatch.dispatch_est_min}m`
      : null;
  const addr = !isPseudo
    ? [dispatch.service_address, dispatch.city].filter(Boolean).join(" · ")
    : "";

  // ── Compact (ESC-style) block — same click / hover-card / right-click
  // handlers as the rich card; only the shell changes. ─────────────────────
  const compactBlock = (
    <button
      type="button"
      onClick={() => {
        endHover();
        onClick(dispatch);
      }}
      onDoubleClick={() => {
        // Double-click opens the full dispatch record (Seth 2026-06-24).
        if (!isPseudo) navigate(`/dispatch/${dispatch.dispatch}?from=board`);
      }}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      onContextMenu={(e) => {
        if (isPseudo) return;
        e.preventDefault();
        endHover();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
      className={cn(
        "block w-full rounded px-1.5 py-1 text-left leading-tight transition-[filter] hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
        stripeColors.bg,
        stripeColors.text,
        conflict?.tone === "warn" && "ring-1 ring-amber-500",
        isDone && "opacity-55",
        cancelAction && "opacity-55 ring-1 ring-red-400",
      )}
    >
      <div className="flex items-baseline gap-1">
        <span className="min-w-0 flex-1 truncate text-[11px] font-semibold">
          {isDone && <span aria-label="completed">✓ </span>}
          {customer}
        </span>
        {timeWindow && (
          <span className="shrink-0 text-[10px] font-bold tabular-nums opacity-90">
            {timeWindow}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="min-w-0 flex-1 truncate text-[10px] opacity-85">
          {dispatch.city ?? ""}
        </span>
        {isPlus && (
          <span
            title="PLUS member — active maintenance plan"
            className="shrink-0 rounded-sm bg-white/80 px-1 text-[9px] font-extrabold tracking-wide text-amber-700"
          >
            PLUS
          </span>
        )}
        {priority && (
          <span className="shrink-0 text-[10px] font-bold uppercase opacity-90">
            {priority}
          </span>
        )}
      </div>
    </button>
  );

  return (
    <>
    {density === "compact" ? compactBlock : (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      onClick={() => {
        endHover();
        onClick(dispatch);
      }}
      onDoubleClick={() => {
        // Double-click opens the full dispatch record (Seth 2026-06-24).
        if (!isPseudo) navigate(`/dispatch/${dispatch.dispatch}?from=board`);
      }}
      onMouseEnter={startHover}
      onMouseLeave={endHover}
      onContextMenu={(e) => {
        if (isPseudo) return;
        e.preventDefault();
        endHover();
        setMenu({ x: e.clientX, y: e.clientY });
      }}
      className={cn(
        "group relative block w-full overflow-hidden rounded-lg border border-l-4 border-black/[0.08] bg-white px-3 py-2.5 text-left shadow-sm transition-all",
        "hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30",
        stripeColors.border,
        conflict?.tone === "warn" && "ring-1 ring-amber-300",
        isDone && "opacity-60",
        cancelAction && "opacity-60 ring-1 ring-red-300",
      )}
    >
      {/* Soft fill of the block's priority/window color (Seth: "I really like
          the feel of the filled-in color — make it more visible on cards").
          A tint layer keeps slate text fully readable on every palette. */}
      <span
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0",
          stripeColors.bg,
          "opacity-[0.22]",
        )}
      />
      <span className="relative block">
      {/* Top row: pills (window + priority) on left, status icon on right */}
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {timeWindow && windowColors && (
            <span
              className={cn(
                "rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                windowColors.bg,
                windowColors.text,
              )}
            >
              {timeWindow}
            </span>
          )}
          {priority && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                stripeColors.bg,
                stripeColors.text,
              )}
            >
              {PseudoIcon && <PseudoIcon className="h-3 w-3" />}
              {priority}
            </span>
          )}
          {/* If neither, but it's a pseudo, show the kind label */}
          {!priority && !timeWindow && PseudoIcon && (
            <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-green-900">
              <PseudoIcon className="h-3 w-3" />
              {pseudoKind}
            </span>
          )}
          {/* Gold plan-member chip — same "take care of them first" signal the
              mobile on-call list wears (BOARD-HD). */}
          {isPlus && (
            <span
              title="PLUS member — active maintenance plan"
              className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800 ring-1 ring-amber-300/70"
            >
              Plus
            </span>
          )}
          {/* Constraint badge — wrong tech / not qualified / requested (BOARD-CONFLICTS-01) */}
          {conflict && (
            <span
              title={conflict.detail}
              className={cn(
                "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                conflict.tone === "warn"
                  ? "bg-amber-100 text-amber-800"
                  : "bg-slate-100 text-slate-600",
              )}
            >
              {conflict.tone === "warn" ? (
                <AlertTriangle className="h-3 w-3" />
              ) : (
                <UserCheck className="h-3 w-3" />
              )}
              {conflict.short}
            </span>
          )}
          {cancelAction && (
            <span
              title={`Cancellation recorded — ${cancelAction.reason_code}`}
              className="inline-flex items-center gap-1 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-red-700"
            >
              <Ban className="h-3 w-3" />
              Cancel req
            </span>
          )}
        </div>
        <StatusBadge status={status} variant="icon" />
      </div>

      {/* Customer name — the hero of the card. It WRAPS, it never gets cut
          off (BOARD-HD v2). */}
      <div
        className={cn(
          "text-[13.5px] font-bold leading-snug",
          isDone ? "text-slate-500 line-through" : "text-slate-900",
        )}
      >
        {customer}
      </div>

      {/* Live field state from the tech's phone (Pegasus Field, M1a). */}
      {!isPseudo && !isDone && fieldRow && (
        <div className="mt-0.5"><FieldChip row={fieldRow} /></div>
      )}

      {/* Street address + city (wraps too), with the estimated length pinned
          top-right — where you're sending him and for how long. */}
      {addr && (
        <div className="mt-0.5 flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 text-[11.5px] leading-snug text-slate-500">
            {addr}
          </div>
          {est && (
            <div
              className="shrink-0 text-[10.5px] font-medium text-slate-400"
              title="Estimated length"
            >
              ~{est}
            </div>
          )}
        </div>
      )}

      {/* Notes preview — AI summary when available (DEC-NEW-AI-03), fallback
          to raw-notes extract otherwise. Two lines now (BOARD-HD): enough to
          actually know what the ticket involves without hovering. */}
      {dispatch.ai_summary ? (
        <div className="mt-1 flex items-center gap-1 text-[11px] italic text-slate-500">
          <Sparkles className="h-3 w-3 flex-shrink-0 text-brand-500" aria-hidden="true" />
          <span className="min-w-0 truncate">{dispatch.ai_summary}</span>
        </div>
      ) : preview && preview !== "—" ? (
        <div className="mt-1 line-clamp-2 text-[11px] leading-snug text-slate-500">
          {preview}
        </div>
      ) : null}

      </span>
    </motion.button>
    )}

    {/* Rich hover card (Task #80) — full notes + address, portal'd to body. */}
    {hoverPos &&
      createPortal(
        <div
          className="pointer-events-none fixed z-[60] w-80"
          style={{
            left: Math.min(hoverPos.x + 16, window.innerWidth - 336),
            top: Math.max(
              8,
              Math.min(hoverPos.y - 40, window.innerHeight - 300),
            ),
          }}
        >
          <div className="rounded-xl border border-hairline bg-white p-3 shadow-e3">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-sm font-semibold text-slate-900">
                {customer}
              </span>
              {isPlus && (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-800">
                  Plus
                </span>
              )}
              {timeWindow && windowColors && (
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                    windowColors.bg,
                    windowColors.text,
                  )}
                >
                  {timeWindow}
                </span>
              )}
              {priority && (
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                    stripeColors.bg,
                    stripeColors.text,
                  )}
                >
                  {priority}
                </span>
              )}
              {conflict && (
                <span
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                    conflict.tone === "warn"
                      ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-600",
                  )}
                >
                  {conflict.short}
                </span>
              )}
            </div>
            {dispatch.city && (
              <div className="mt-1 text-[11px] text-slate-500">{dispatch.city}</div>
            )}
            {dispatch.service_address && (
              <div className="text-[11px] text-slate-500">
                {dispatch.service_address}
              </div>
            )}
            {dispatch.notes && (
              <div className="mt-1.5 max-h-48 overflow-hidden whitespace-pre-wrap border-t border-hairline pt-1.5 text-[11px] leading-snug text-slate-600">
                {dispatch.notes}
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}

    {/* Right-click quick actions (Task #80) */}
    {menu &&
      createPortal(
        <div
          className="fixed inset-0 z-[70]"
          onClick={() => setMenu(null)}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu(null);
          }}
        >
          <div
            className="absolute w-56 overflow-hidden rounded-lg border border-hairline bg-white py-1 shadow-e3"
            style={{
              left: Math.min(menu.x, window.innerWidth - 240),
              top: Math.min(menu.y, window.innerHeight - 280),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="truncate border-b border-hairline px-3 py-1.5 text-[11px] font-semibold text-slate-500">
              {customer}
            </div>
            <button
              type="button"
              onClick={() => {
                setMenu(null);
                navigate(`/dispatch/${dispatch.dispatch}?from=board`);
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
            >
              <ExternalLink className="h-3.5 w-3.5 text-slate-400" />
              Open dispatch
            </button>
            <button
              type="button"
              onClick={() => {
                setMenu(null);
                navigate(`/customers/${dispatch.custno}`);
              }}
              title="Pull up this customer's record"
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
            >
              <UserRound className="h-3.5 w-3.5 text-slate-400" />
              Qualify
            </button>
            <div className="my-1 border-t border-hairline" />
            <button
              type="button"
              onClick={() => {
                setMenu(null);
                setTicketKind("order_parts");
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
            >
              <Package className="h-3.5 w-3.5 text-slate-400" />
              Needs part…
            </button>
            <button
              type="button"
              onClick={() => {
                setMenu(null);
                setTicketKind("return_visit");
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
            >
              <RotateCcw className="h-3.5 w-3.5 text-slate-400" />
              Needs return visit…
            </button>
            <div className="my-1 border-t border-hairline" />
            {cancelAction ? (
              <button
                type="button"
                onClick={() => {
                  voidAction.mutate(cancelAction.id);
                  setMenu(null);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
              >
                <RotateCcw className="h-3.5 w-3.5 text-slate-400" />
                Undo cancellation
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setMenu(null);
                  setShowCancel(true);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
              >
                <Ban className="h-3.5 w-3.5" />
                Cancel dispatch…
              </button>
            )}
            <div className="my-1 border-t border-hairline" />
            {dispatch.service_address && (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(
                    `${dispatch.service_address}${dispatch.city ? `, ${dispatch.city}` : ""}`,
                  );
                  setMenu(null);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
              >
                <MapPin className="h-3.5 w-3.5 text-slate-400" />
                Copy address
              </button>
            )}
            {dispatch.customer_phone && (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(dispatch.customer_phone ?? "");
                  setMenu(null);
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50"
              >
                <Phone className="h-3.5 w-3.5 text-slate-400" />
                Copy phone
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}

    {/* Quick part/return ticket from this dispatch (TICKET-01b) */}
    {ticketKind &&
      createPortal(
        <FromDispatchTicketModal
          dispatch={dispatch}
          kind={ticketKind}
          onClose={() => setTicketKind(null)}
        />,
        document.body,
      )}

    {/* Cancel-with-reason (CANCEL-01) */}
    {showCancel &&
      createPortal(
        <CancelDispatchModal
          dispatch={dispatch}
          onClose={() => setShowCancel(false)}
        />,
        document.body,
      )}
    </>
  );
}
